import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Link,
  Switch,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { Theme, createTheme, styled } from '@mui/material/styles';
import { useState } from 'react';

const COLORS = {
  text: '#050a14',
  actionBlue: '#2d68ea',
  actionBlueHover: '#2257cf',
  linkBlue: '#2350d8',
  divider: '#dfdee3',
  cardBorder: '#e4e5e9',
  inputBorder: '#cfd2d9',
  inputBorderHover: '#9aa1ad',
  switchOff: '#b8bec7',
  infoBackground: '#edf2fa',
};

/* ════════════════════ Gotham Font ════════════════════ */

// Licensed Gotham files served by ziplyfiber.com (Sitecore). Same origin as My Account in
// prod; where they don't load (e.g. localhost) the page falls back to DM Sans.
const GOTHAM_FONTS_PATH = '/assets/residential/fonts';
const GOTHAM_FONT_FAMILY = 'Gotham, "DM Sans", sans-serif';

// Book for regular text; Medium covers 500-700 so bold text doesn't get a synthesized weight.
export const GOTHAM_FONT_FACES = `
  @font-face {
    font-family: 'Gotham';
    src: url('${GOTHAM_FONTS_PATH}/Gotham-Book.woff2') format('woff2'),
      url('${GOTHAM_FONTS_PATH}/Gotham-Book.woff') format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'Gotham';
    src: url('${GOTHAM_FONTS_PATH}/Gotham-Medium.woff2') format('woff2'),
      url('${GOTHAM_FONTS_PATH}/Gotham-Medium.woff') format('woff');
    font-weight: 500 700;
    font-style: normal;
    font-display: swap;
  }
`;

// Each typography variant carries its own fontFamily, so all of them are switched, not just the default.
export const withGothamFont = (outerTheme: Theme) =>
  createTheme(outerTheme, {
    typography: Object.fromEntries([
      ['fontFamily', GOTHAM_FONT_FAMILY],
      ...Object.entries(outerTheme.typography)
        .filter(([, style]) => typeof style === 'object' && style !== null && 'fontFamily' in style)
        .map(([variant]) => [variant, { fontFamily: GOTHAM_FONT_FAMILY }]),
    ]),
  });

/* ════════════════════ Page Layout ════════════════════ */

// Fixed viewport height like the main login's ContainerStack, so the shared Footer's
// vertical divider (height: 100%) resolves the same way on both screens.
export const FiberFirstPage = styled(Box)(({ theme }) => ({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  color: COLORS.text,
  // Plain elements (labels, spans) inherit this instead of the app-wide body font
  fontFamily: theme.typography.fontFamily,
}));

export const PageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: 1200,
  margin: '0 auto',
  padding: '0 16px',

  [theme.breakpoints.up('md')]: {
    padding: '0 56px',
  },
}));

export const HeaderBar = styled('header')({
  backgroundColor: '#fff',
  borderBottom: `1px solid ${COLORS.divider}`,
});

export const HeaderInner = styled(PageContainer)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  minHeight: 66,

  [theme.breakpoints.down('md')]: {
    paddingLeft: 32,
  },
}));

export const BrandLogo = styled('img')(({ theme }) => ({
  display: 'block',
  width: 142,
  height: 'auto',

  [theme.breakpoints.down('md')]: {
    width: 126,
  },
}));

export const MainContent = styled('main')(({ theme }) => ({
  flex: 1,
  padding: '20px 0 40px',

  [theme.breakpoints.up('md')]: {
    padding: '56px 0',
  },
}));

export const ContentGrid = styled(PageContainer)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  rowGap: 20,

  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 460px)',
    columnGap: 48,
    alignItems: 'start',
  },
}));

/* ════════════════════ Hero ════════════════════ */

export const HeroSection = styled(Box)(({ theme }) => ({
  padding: '0 16px',

  [theme.breakpoints.up('md')]: {
    padding: '54px 0 0',
  },
}));

export const HeroTitle = styled(Typography)(({ theme }) => ({
  fontSize: 40,
  fontWeight: 700,
  lineHeight: 1.1,
  letterSpacing: '-0.02em',
  color: COLORS.text,

  // Same brand green as the "Ziply" span in the main login's "Welcome to Ziply Fiber" heading.
  '& span': {
    display: 'block',
    color: theme.palette.primary.main,
  },

  [theme.breakpoints.up('md')]: {
    fontSize: 60,
    lineHeight: 1,
  },
}));

export const HeroSubtitle = styled(Typography)(({ theme }) => ({
  marginTop: 16,
  fontSize: 18,
  fontWeight: 700,
  lineHeight: 1.3,
  color: COLORS.text,

  [theme.breakpoints.up('md')]: {
    marginTop: 28,
    fontSize: 24,
  },
}));

export const HeroBody = styled(Typography)(({ theme }) => ({
  marginTop: 8,
  fontSize: 17,
  lineHeight: 1.45,
  color: COLORS.text,

  [theme.breakpoints.up('md')]: {
    marginTop: 12,
    fontSize: 20,
    lineHeight: 1.5,
  },
}));

/* ════════════════════ Form Card ════════════════════ */

export const FormCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  border: `1px solid ${COLORS.cardBorder}`,
  borderRadius: 4,
  boxShadow: '0 2px 8px rgba(16, 24, 40, 0.06)',
  padding: '24px 16px',

  [theme.breakpoints.up('md')]: {
    padding: '38px 20px 24px',
  },
}));

export const FieldLabel = styled('label')({
  display: 'block',
  marginBottom: 6,
  fontSize: 15,
  fontWeight: 500,
  color: COLORS.text,
});

export const FieldGroup = styled(Box)({
  '& + &': {
    marginTop: 22,
  },
});

export const ForgotPasswordRow = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 10,
});

export const ForgotPasswordLink = styled(Link)({
  fontSize: 13,
  fontWeight: 500,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: COLORS.linkBlue,
  cursor: 'pointer',
}) as typeof Link;

export const KeepLoggedInRow = styled(Box)({
  marginTop: 24,
  '& .MuiFormControlLabel-root': {
    marginLeft: 0,
  },
  '& .MuiFormControlLabel-label': {
    fontSize: 14,
    color: COLORS.text,
  },
});

export const KeepLoggedInSwitch = styled(Switch)(({ theme }) => ({
  width: 50,
  height: 28,
  padding: 0,
  marginRight: 12,

  '& .MuiSwitch-switchBase': {
    padding: 3,
    '&.Mui-checked': {
      transform: 'translateX(22px)',
      '& + .MuiSwitch-track': {
        backgroundColor: COLORS.actionBlue,
        opacity: 1,
      },
    },
    '&.Mui-focusVisible + .MuiSwitch-track': {
      outline: `2px solid ${COLORS.actionBlue}`,
      outlineOffset: 2,
    },
  },

  '& .MuiSwitch-track': {
    borderRadius: 14,
    backgroundColor: COLORS.switchOff,
    opacity: 1,
  },
}));

export const SwitchThumb = styled('span')({
  width: 22,
  height: 22,
  borderRadius: '50%',
  backgroundColor: '#fff',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: COLORS.actionBlue,
});

export const LoginButton = styled(Button)({
  marginTop: 20,
  height: 52,
  borderRadius: 999,
  backgroundColor: COLORS.actionBlue,
  fontSize: 18,
  fontWeight: 500,
  letterSpacing: 0.5,
  boxShadow: 'none',

  '&:hover': {
    backgroundColor: COLORS.actionBlueHover,
    boxShadow: 'none',
  },
});

export const FormDivider = styled(Box)({
  margin: '20px 0 24px',
  borderTop: `1px solid ${COLORS.divider}`,
});

export const InfoBox = styled(Box)(({ theme }) => ({
  backgroundColor: COLORS.infoBackground,
  borderRadius: 4,
  padding: 16,

  [theme.breakpoints.up('md')]: {
    padding: 20,
  },
}));

export const InfoHeading = styled(Typography)(({ theme }) => ({
  marginBottom: 4,
  fontSize: 19,
  fontWeight: 700,
  lineHeight: 1.3,
  color: COLORS.text,

  [theme.breakpoints.up('md')]: {
    fontSize: 20,
  },
}));

export const InfoBody = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  lineHeight: 1.45,
  color: COLORS.text,

  '& strong': {
    fontWeight: 700,
  },

  [theme.breakpoints.up('md')]: {
    fontSize: 17,
  },
}));

/* ════════════════════ Help ════════════════════ */

export const HelpRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 14,
  marginTop: 32,
  fontSize: 15,

  [theme.breakpoints.up('md')]: {
    marginTop: 48,
  },
}));

export const HelpCenterLink = styled(Link)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: 0.3,
  textTransform: 'uppercase',
  color: COLORS.linkBlue,
});

/* ════════════════════ FiberFirst TextField ════════════════════ */

const FIBERFIRST_INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 48,
    borderRadius: '4px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: COLORS.inputBorder },
    '&:hover fieldset': { borderColor: COLORS.inputBorderHover },
    '&.Mui-focused fieldset': { borderColor: COLORS.actionBlue },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
  },
} as const;

interface FiberFirstTextFieldProps extends Omit<TextFieldProps, 'fullWidth' | 'sx' | 'label'> {
  id: string;
  label: string;
  isPassword?: boolean;
}

export const FiberFirstTextField = ({
  id,
  label,
  isPassword = false,
  ...textFieldProps
}: FiberFirstTextFieldProps) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <FieldGroup>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <TextField
        id={id}
        fullWidth
        sx={FIBERFIRST_INPUT_SX}
        type={isPassword && !showPassword ? 'password' : 'text'}
        InputProps={{
          endAdornment: isPassword ? (
            <IconButton
              edge="end"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
            </IconButton>
          ) : undefined,
        }}
        {...textFieldProps}
      />
    </FieldGroup>
  );
};
